import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useStore';
import { authAPI, secondoFattoreAPI } from '../services/api';
import '../styles/Login.css';

/* Il login e' in due tempi per chi ha la verifica in due passaggi
   attiva: password, e poi le sei cifre. Il secondo passo NON e' una
   schermata che il frontend decide di mostrare - la sessione a quel
   punto non esiste ancora, il server ha dato solo un gettone che vive
   due minuti e serve a una rotta sola. Se un domani qualcuno
   "semplificasse" saltando questa schermata, non entrerebbe: non
   avrebbe niente con cui entrare. */

const DURATA_GETTONE_SECONDI = 120;

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    /* Un blocco per troppi tentativi NON e' una password sbagliata, e si
       mostra diverso: chi lo legge come «credenziali non valide» finisce
       per reimpostare una password che era giusta. */
    const [bloccato, setBloccato] = useState(false);

    const mostraErrore = (err, ripiego) => {
        const e429 = err?.response?.status === 429
                  || err?.code === 'UTENZA_BLOCCATA' || err?.code === 'INDIRIZZO_BLOCCATO';
        setBloccato(e429);
        setError(e429
            ? (err?.message || 'Accesso temporaneamente bloccato per troppi tentativi: riprova fra un quarto d\'ora.')
            : (err?.message || ripiego));
    };
    const [loading, setLoading] = useState(false);

    // Secondo passo
    const [gettone, setGettone] = useState(null);
    const [codice, setCodice] = useState('');
    const [recupero, setRecupero] = useState('');
    const [conRecupero, setConRecupero] = useState(false);
    const [restano, setRestano] = useState(DURATA_GETTONE_SECONDI);

    const navigate = useNavigate();
    const login = useAuthStore((state) => state.login);
    const campoCodice = useRef(null);

    const entra = (dati) => {
        const { token, user } = dati;
        login(user, token);
        /* La password provvisoria viene prima di tutto: chi deve ancora
           cambiarla non arriva alla dashboard nemmeno adesso.
           Subito dopo, chi e' obbligato al secondo fattore e non l'ha
           attivato va al profilo, dove c'e' il pannello per farlo. */
        if (user?.deve_cambiare_password) return navigate('/cambia-password', { replace: true });
        if (dati.secondo_fattore?.da_attivare) return navigate('/profilo', { replace: true });
        /* Il telefono dello studio ha una pagina sola. */
        if (user?.ruolo === 'acquisizione') return navigate('/acquisizione', { replace: true });
        return navigate('/dashboard', { replace: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const response = await authAPI.login({ username, password });
            if (response.data.success) {
                const dati = response.data.data;
                if (dati.serve_secondo_fattore) {
                    setGettone(dati.gettone);
                    setRestano(DURATA_GETTONE_SECONDI);
                    setPassword('');
                    return;
                }
                entra(dati);
            }
        } catch (err) {
            mostraErrore(err, 'Credenziali non valide.');
        } finally {
            setLoading(false);
        }
    };

    /* Il conto alla rovescia non e' un vezzo: il gettone scade in due
       minuti, e senza questo l'utente vedrebbe solo un «codice non
       valido» per un codice che invece era giusto. */
    useEffect(() => {
        if (!gettone) return undefined;
        const t = setInterval(() => {
            setRestano((s) => {
                if (s <= 1) {
                    clearInterval(t);
                    setGettone(null);
                    setCodice('');
                    setRecupero('');
                    setConRecupero(false);
                    setError('Tempo scaduto. Rifai il login.');
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [gettone]);

    useEffect(() => {
        if (gettone && campoCodice.current) campoCodice.current.focus();
    }, [gettone, conRecupero]);

    const inviaSecondoFattore = async (valore) => {
        setError('');
        setLoading(true);
        try {
            const corpo = conRecupero
                ? { gettone, recupero: valore }
                : { gettone, codice: valore };
            const response = await secondoFattoreAPI.verifica(corpo);
            if (response.data.success) entra(response.data.data);
        } catch (err) {
            mostraErrore(err, 'Codice non valido.');
            setCodice('');
            setRecupero('');
            /* Gettone bruciato o scaduto: non ha senso lasciare la
               schermata delle cifre, non c'e' piu' niente da spendere. */
            if (err.code === 'GETTONE_BRUCIATO' || err.code === 'GETTONE_SCADUTO') {
                setGettone(null);
                setConRecupero(false);
            }
        } finally {
            setLoading(false);
        }
    };

    // Invio automatico alla sesta cifra: e' il gesto che ci si aspetta,
    // e toglie un clic a chi entra venti volte al giorno.
    const cambiaCodice = (testo) => {
        const cifre = testo.replace(/\D/g, '').slice(0, 6);
        setCodice(cifre);
        if (cifre.length === 6 && !loading) inviaSecondoFattore(cifre);
    };

    const mm = String(Math.floor(restano / 60));
    const ss = String(restano % 60).padStart(2, '0');

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    {/* Il marchio porta gia' il proprio payoff: un
                        secondo sottotitolo sotto sarebbero due frasi che
                        dicono chi siamo, una sopra l'altra. */}
                    <img className="login-logo"
                         src="/images/logo-rimedichub.png"
                         alt="RimedicHUB — Rimedical, sempre un passo avanti"
                         width="340" height="160" />
                </div>

                {!gettone ? (
                    <>
                        <form onSubmit={handleSubmit} className="login-form">
                            {error && <div className={`error-message ${bloccato ? 'error-message--bloccato' : ''}`}>{bloccato && <b>Accesso bloccato. </b>}{error}</div>}
                            <div className="form-group">
                                <label htmlFor="username">Username</label>
                                <input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Inserisci username" required disabled={loading} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Inserisci password" required disabled={loading} />
                            </div>
                            <button type="submit" className="btn-login" disabled={loading}>
                                {loading ? 'Accesso in corso...' : 'Accedi'}
                            </button>
                        </form>

                        <div className="login-sotto">
                            <button
                                type="button"
                                className="login-link"
                                onClick={() => navigate('/password-dimenticata')}
                                disabled={loading}
                            >
                                Password dimenticata?
                            </button>
                        </div>
                    </>
                ) : (
                    <form
                        className="login-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            inviaSecondoFattore(conRecupero ? recupero : codice);
                        }}
                    >
                        {error && <div className={`error-message ${bloccato ? 'error-message--bloccato' : ''}`}>{bloccato && <b>Accesso bloccato. </b>}{error}</div>}

                        <p className="sf-titolo">Codice di verifica</p>
                        <p className="sf-spiega">
                            {conRecupero
                                ? 'Inserisci uno degli otto codici di recupero. Vale una volta sola.'
                                : 'Apri l’app di autenticazione e inserisci le sei cifre che mostra.'}
                        </p>

                        {conRecupero ? (
                            <div className="form-group">
                                <label htmlFor="recupero">Codice di recupero</label>
                                <input
                                    type="text"
                                    id="recupero"
                                    ref={campoCodice}
                                    className="sf-campo-recupero"
                                    value={recupero}
                                    onChange={(e) => setRecupero(e.target.value.toUpperCase())}
                                    placeholder="XXXX-XXXX"
                                    autoComplete="one-time-code"
                                    maxLength={9}
                                    disabled={loading}
                                />
                            </div>
                        ) : (
                            <div className="form-group">
                                <label htmlFor="codice">Sei cifre</label>
                                <input
                                    type="text"
                                    id="codice"
                                    ref={campoCodice}
                                    className="sf-campo-cifre"
                                    value={codice}
                                    onChange={(e) => cambiaCodice(e.target.value)}
                                    placeholder="000000"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    disabled={loading}
                                />
                            </div>
                        )}

                        <p className="sf-tempo">
                            {restano > 0
                                ? `Il codice va inserito entro ${mm}:${ss}`
                                : 'Tempo scaduto.'}
                        </p>

                        <button type="submit" className="btn-login" disabled={loading}>
                            {loading ? 'Verifica in corso...' : 'Entra'}
                        </button>

                        <div className="login-sotto">
                            <button
                                type="button"
                                className="login-link"
                                onClick={() => { setConRecupero((v) => !v); setError(''); setCodice(''); setRecupero(''); }}
                                disabled={loading}
                            >
                                {conRecupero ? 'Torna alle sei cifre' : 'Usa un codice di recupero'}
                            </button>
                            <button
                                type="button"
                                className="login-link"
                                onClick={() => { setGettone(null); setCodice(''); setRecupero(''); setConRecupero(false); setError(''); }}
                                disabled={loading}
                            >
                                Ricomincia
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default Login;
